// src/services/auth.service.ts
import axiosInstance from "../middlewares/axiosInstance";
import {
  LoginCredentials,
  AuthResponse,
  User,
  ApiResponse,
  PasswordResetRequest,
  PasswordResetConfirm,
  PasswordResetResponse,
} from "../modules/auth/types/auth.types";
import { RegisterFormValues } from "../modules/auth/types/index";
import { AuthError } from "../modules/auth/utils/auth.error";
import { AuthErrorType } from "../modules/auth/types/auth.error.types";

interface PublicRegisterResponse {
  success: boolean;
  data: {
    id: string;
    email: string;
    role: string;
    first_name: string;
    last_name: string;
  };
  message: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
        "/auth/login",
        credentials
      );

      return response.data.data;
    } catch (error: any) {
      console.error("Login error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw new AuthError(
        "Invalid email or password",
        AuthErrorType.INVALID_CREDENTIALS
      );
    }
  },

  async loginWithPin(email: string, pos_pin: string): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
        "/auth/login-with-pin",
        { email, pos_pin }
      );

      return response.data.data;
    } catch (error: any) {
      console.error("PIN login error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw new AuthError(
        "Invalid email or PIN",
        AuthErrorType.INVALID_CREDENTIALS
      );
    }
  },

  async logout(refreshToken: string): Promise<void> {
    try {
      await axiosInstance.post("/auth/logout", { refreshToken });
    } catch (error) {
      throw new AuthError(
        "Failed to logout. Please try again.",
        AuthErrorType.UNAUTHORIZED
      );
    }
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      console.log(
        "Attempting token refresh with token:",
        refreshToken.substring(0, 10) + "..."
      );
      const response = await axiosInstance.post<AuthResponse>(
        "/auth/refresh-token",
        {
          refreshToken,
        }
      );
      console.log("Token refresh response:", {
        success: true,
        hasNewAccessToken: !!response.data.accessToken,
        hasNewRefreshToken: !!response.data.refreshToken,
      });
      return response.data;
    } catch (error: any) {
      console.error("Token refresh error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        headers: error.response?.headers,
      });
      throw new AuthError(
        "Session expired. Please login again.",
        AuthErrorType.SESSION_EXPIRED
      );
    }
  },

  async register(data: RegisterFormValues): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        "/auth/register",
        data
      );
      return response.data;
    } catch (error: any) {
      // Check if error is due to existing email
      if (error.response?.status === 409) {
        throw new AuthError(
          "Email already exists",
          AuthErrorType.REGISTRATION_FAILED
        );
      }
      throw new AuthError(
        "Registration failed. Please try again.",
        AuthErrorType.REGISTRATION_FAILED
      );
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response = await axiosInstance.post<ApiResponse<User>>(
        "/auth/current-user"
      );
      return response.data.data;
    } catch (error: any) {
      throw new AuthError(
        "Failed to fetch user details",
        AuthErrorType.UNAUTHORIZED
      );
    }
  },

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      await axiosInstance.post("/auth/change-password", {
        currentPassword,
        newPassword,
      });
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new AuthError(
          "Current password is incorrect",
          AuthErrorType.PASSWORD_CHANGE_FAILED
        );
      }
      throw new AuthError(
        "Failed to change password. Please try again.",
        AuthErrorType.PASSWORD_CHANGE_FAILED
      );
    }
  },

  async publicRegister(data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phoneNumber?: string;
  }): Promise<PublicRegisterResponse> {
    try {
      // Format the data for the API
      const formattedData = {
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phoneNumber: data.phoneNumber || "",
        business_name: "",
        role: "store_owner",
        store_id: "",
        is_store_owner: true,
        is_super_admin: false,
        is_active: false,
        permissions: [
          "view_products",
          "edit_products",
          "view_orders",
          "edit_orders",
        ],
      };

      const response = await axiosInstance.post<PublicRegisterResponse>(
        "/auth/public-register",
        formattedData
      );

      return response.data;
    } catch (error: any) {
      // Check if error is a network error
      if (!error.response) {
        throw new AuthError(
          "Unable to connect to the server. Please check your internet connection and try again.",
          AuthErrorType.NETWORK_ERROR
        );
      }

      // Check if error is due to existing email
      if (error.response?.status === 409) {
        throw new AuthError(
          "Email already exists",
          AuthErrorType.REGISTRATION_FAILED
        );
      }

      // Handle other specific status codes
      if (error.response?.status === 400) {
        throw new AuthError(
          error.response.data?.error?.message || "Invalid registration data",
          AuthErrorType.VALIDATION_ERROR
        );
      }

      if (error.response?.status === 500) {
        throw new AuthError(
          "Server error. Please try again later.",
          AuthErrorType.SERVER_ERROR
        );
      }

      // Generic error
      throw new AuthError(
        "Registration failed. Please try again.",
        AuthErrorType.REGISTRATION_FAILED
      );
    }
  },

  // Helper method to check if error is auth-related
  isAuthError(error: any): boolean {
    return error instanceof AuthError;
  },
};
